// The online store (online.* contracts): the one read and the one write the Online Store pages make. Each tab saves
// only the fields it owns, laid over the store as it stands (`online.upsertStore` with a partial `changes`).
import type { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import type { ListCardRefundsToSettle, ListLateSaleAlerts, OnlineStoreFields, OnlineStoreView } from '@radish/contracts';
import type { PermissionKey } from '@radish/contracts';
import { mayOpen } from '../../app/page-permissions.ts';
import { unwrap, useApi, useCommand, useLocationQuery, type CallFailure } from '../../data/api.tsx';

export type OnlineStore = z.output<typeof OnlineStoreView>;
export type StoreFields = z.output<typeof OnlineStoreFields>;
export type DayHours = StoreFields['menuHours'][number];
export type PrepTime = StoreFields['prepTimes'][number];
export type Banner = StoreFields['banners'][number];
export type Closure = StoreFields['specialClosures'][number];

const STORE = 'online.store.get';
const SUMMARY = 'online.store.summary';

/** The whole store, for the Online Store pages (Manage Online Store, as legacy's online-store routes). */
export function useOnlineStore(locationId: string) {
  return useLocationQuery(locationId, STORE, { locationId }, (c) => c.queries.online.store.get({ locationId: locationId as never }));
}

/** Whether the location has a store, and its business hours, for pages outside the Online Store (no payment fields). */
export function useOnlineStoreSummary(locationId: string) {
  return useLocationQuery(locationId, SUMMARY, { locationId }, (c) => c.queries.online.store.summary({ locationId: locationId as never }));
}

/** Legacy `online_store_enabled`: the location has an online store (`onlineStore()->exists()`). Lists show Online Orders toggles only then. */
export function useHasOnlineStore(locationId: string): boolean {
  return (useOnlineStoreSummary(locationId).data?.store ?? null) !== null;
}

export function useSaveOnlineStore(locationId: string) {
  return useCommand(
    locationId,
    (c, input: { readonly onlineStoreId: string; readonly changes: Partial<StoreFields> }, call) =>
      c.commands.online.upsertStore({ onlineStoreId: input.onlineStoreId as never, locationId: locationId as never, changes: input.changes as never }, call),
    [STORE, SUMMARY],
  );
}

/** Business Profile's Business Hours tab (legacy `POST /v1/online-store/business-hours`), gated as that tab is. */
export function useSetBusinessHours(locationId: string) {
  return useCommand(
    locationId,
    (c, input: { readonly onlineStoreId: string; readonly businessHours: readonly DayHours[] }, call) =>
      c.commands.online.setBusinessHours({ onlineStoreId: input.onlineStoreId as never, locationId: locationId as never, businessHours: [...input.businessHours] }, call),
    [STORE, SUMMARY],
  );
}

/** The API Keys tab's private key (A34): sent once, sealed in the cloud, never read back (the store shows its last 4). */
export function useSetGatewayPrivateKey(locationId: string) {
  return useCommand(
    locationId,
    (c, input: { readonly onlineStoreId: string; readonly privateKey: string | null }, call) =>
      c.commands.online.setGatewayPrivateKey({ onlineStoreId: input.onlineStoreId as never, locationId: locationId as never, privateKey: input.privateKey }, call),
    [STORE, SUMMARY],
  );
}

export type VoiceSettings = NonNullable<StoreFields['voice']>;

export interface VoiceFilters {
  readonly outcome: string | null;
  readonly hasUnresolved: boolean;
}

/** The Conversations list, a page at a time (cloud only; legacy server-paginates it). */
export function useVoiceConversations(locationId: string, filters: VoiceFilters, page: number, perPage: 10 | 25 | 50, enabled = true) {
  const input = { locationId, outcome: filters.outcome, hasUnresolved: filters.hasUnresolved, page, perPage };
  return useLocationQuery(locationId, 'online.voiceConversations.list', input, (c) => c.queries.online.voiceConversations.list(input as never), enabled);
}

export function useVoiceConversation(locationId: string, conversationId: string) {
  const input = { locationId, conversationId };
  return useLocationQuery(locationId, 'online.voiceConversations.get', input, (c) => c.queries.online.voiceConversations.get(input as never));
}

// ---- late card charges (gate 18 of the Radish Direct plan; A189, L936) ------------------------------------------------
export type LateSaleAlerts = z.output<typeof ListLateSaleAlerts.output>;
export type LateSaleAlert = LateSaleAlerts['alerts'][number];

const LATE_SALES = 'online.lateSales.list';
/** The count on the sidebar and the tab updates by itself (no refresh button, A189): this often, and on focus. */
const LATE_SALES_REFRESH_MS = 60_000;

/**
 * The location's late card-charge alerts, for the Online Orders tab and its counts. Asked only of the cloud (a node's
 * back office has none to show) and only by someone who may open the tab (Manage Online Store and its Orders key).
 */
export function useLateSaleAlerts(locationId: string, can: (p: PermissionKey) => boolean) {
  const { client, environment } = useApi();
  const enabled = environment.profile === 'cloud' && mayOpen('/l/$slug/online-store/orders', can);
  const input = { locationId };
  return useQuery<LateSaleAlerts, CallFailure>({
    queryKey: [environment.profile, locationId, LATE_SALES, input],
    queryFn: () => unwrap(() => client.queries.online.lateSales.list({ locationId: locationId as never })),
    enabled,
    refetchInterval: LATE_SALES_REFRESH_MS,
    refetchOnWindowFocus: true,
  });
}

/** Mark as handled (`online.markLateSaleHandled`): an optional note; who and when are recorded by the cloud. */
export function useMarkLateSaleHandled(locationId: string) {
  return useCommand(
    locationId,
    (c, input: { readonly alertId: string; readonly onlineOrderId: string; readonly note: string | null }, call) =>
      c.commands.online.markLateSaleHandled({ alertId: input.alertId, onlineOrderId: input.onlineOrderId as never, locationId: locationId as never, note: input.note }, call),
    [LATE_SALES],
  );
}

/** Open alerts, for the red count on the sidebar's Online Store and the Online Orders tab; undefined until known. */
export function useOpenLateSales(locationId: string, can: (p: PermissionKey) => boolean): number | undefined {
  return useLateSaleAlerts(locationId, can).data?.open;
}

// ---- online card refunds to settle by hand (ops plan 2026-10-29-settle-unconfirmed-refunds, step 4; L1044) -----------
export type CardRefundsToSettle = z.output<typeof ListCardRefundsToSettle.output>;
export type CardRefundToSettle = CardRefundsToSettle['refunds'][number];
type CardRefundSettlement = { readonly issued: true; readonly gatewayRef: string } | { readonly issued: false };

const REFUNDS_TO_SETTLE = 'online.listCardRefundsToSettle';

/**
 * The location's online card refunds waiting for the gateway's answer, and those settled by hand in the last 90 business
 * days. Asked only of the cloud (only it journals them) and only by someone with the void permission, as the settle needs.
 */
export function useCardRefundsToSettle(locationId: string, can: (p: PermissionKey) => boolean) {
  const { client, environment } = useApi();
  const enabled = environment.profile === 'cloud' && can('orders.void');
  const input = { locationId };
  return useQuery<CardRefundsToSettle, CallFailure>({
    queryKey: [environment.profile, locationId, REFUNDS_TO_SETTLE, input],
    queryFn: () => unwrap(() => client.queries.online.listCardRefundsToSettle({ locationId: locationId as never })),
    enabled,
    refetchInterval: LATE_SALES_REFRESH_MS,
    refetchOnWindowFocus: true,
  });
}

/** Settle by hand (`online.settleCardRefund`): Refunded with the portal's reference, or Not refunded. */
export function useSettleCardRefund(locationId: string) {
  return useCommand(
    locationId,
    (c, input: { readonly refundTenderId: string; readonly settlement: CardRefundSettlement }, call) =>
      c.commands.online.settleCardRefund({ locationId: locationId as never, refundTenderId: input.refundTenderId, settlement: input.settlement }, call),
    [REFUNDS_TO_SETTLE],
  );
}
